/**
 * Sprint 207X — My Completed Milestones ribbon.
 *
 * Horizontal scroll bar shown on the AvA Library page directly between
 * "My Trainers" and "Create Custom Practice Partner". Pulls the teen's
 * completed challenge_runs (joined with challenge_library + vault_items
 * for reward thumbnails) from GET /api/teen/milestones.
 *
 * Silently hides when the teen has zero completions so the page doesn't
 * carry an empty "🏆 nothing yet" tile that drains motivation.
 */
import React, { useEffect, useState } from 'react';
import { Trophy, Sparkles, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const MilestoneRibbon = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.request('/api/teen/milestones');
        if (cancelled) return;
        setRows(data.rows || []);
        setTotal(data.total_completed || 0);
      } catch (_e) {
        // Silent — endpoint requires auth; non-teens just see no ribbon.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="my-4 px-2" data-testid="milestone-ribbon-loading">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading milestones…
        </div>
      </div>
    );
  }

  if (rows.length === 0) return null;

  return (
    <div className="mb-6" data-testid="milestone-ribbon">
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
          <Trophy size={16} className="text-amber-500" />
          My Completed Milestones
          <span className="text-xs font-normal text-gray-400">· {total} total</span>
        </h3>
        <span className="text-[11px] text-gray-400">scroll →</span>
      </div>
      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth"
        style={{ scrollbarWidth: 'thin' }}
        data-testid="milestone-ribbon-scroll"
      >
        {rows.map((m) => {
          const seq = m.sequence ?? null;
          return (
            <div
              key={m.run_id || m.challenge_id}
              className="flex-shrink-0 w-44 bg-gradient-to-br from-amber-50 via-white to-pink-50 border border-amber-200/70 rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`milestone-tile-${m.challenge_id}`}
              title={m.title}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                  {seq !== null ? `#${seq}` : 'Done'}
                </span>
                <span className="text-[10px] text-gray-400">{formatDate(m.completed_at)}</span>
              </div>
              <div className="flex items-center gap-2.5">
                {m.reward?.thumbnail_url ? (
                  <img
                    src={m.reward.thumbnail_url}
                    alt={m.title}
                    className="w-10 h-10 rounded-lg object-cover border border-amber-200 flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-pink-400 flex items-center justify-center flex-shrink-0">
                    <Sparkles size={16} className="text-white" />
                  </div>
                )}
                <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2">
                  {m.title}
                </p>
              </div>
              {m.reward?.mint_status && (
                <p className="mt-2 text-[10px] uppercase tracking-wide font-bold text-gray-500">
                  {m.reward.mint_status === 'minted' ? '✓ Minted NFT' : `${m.reward.mint_status}`}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MilestoneRibbon;
